import * as RadixSelect from "@radix-ui/react-select";
import type { ReactNode } from "react";

import { Check, ChevronDown, ChevronRight, Close } from "./icons";
import { cn } from "./cn";

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps {
  id?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
}

const triggerClassName = cn(
  "inline-flex w-full h-(--control-h) min-h-(--control-min-h) items-center justify-between gap-2 rounded-lg",
  "bg-sunken px-3.5 text-body text-fg data-[placeholder]:text-fg-subtle",
  "focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-focus",
  "aria-[invalid=true]:outline-[1.5px] aria-[invalid=true]:outline-offset-0 aria-[invalid=true]:outline-error",
  "disabled:cursor-not-allowed disabled:text-fg-disabled",
);

const contentClassName = cn(
  "z-50 overflow-hidden rounded-xl bg-raised p-1 shadow-sheet",
);

const itemClassName = cn(
  "flex min-h-11 cursor-pointer items-center gap-2 rounded-lg px-3 text-body text-fg outline-none",
  "data-[highlighted]:bg-sunken data-[state=checked]:bg-accent-subtle",
);

export function Select({
  options,
  placeholder,
  value,
  onValueChange,
  disabled,
  id,
  ...aria
}: SelectProps) {
  return (
    <RadixSelect.Root
      value={value}
      onValueChange={onValueChange}
      disabled={disabled}
    >
      <RadixSelect.Trigger id={id} className={triggerClassName} {...aria}>
        <RadixSelect.Value placeholder={placeholder} />
        <RadixSelect.Icon>
          <ChevronDown size={18} className="text-fg-subtle" />
        </RadixSelect.Icon>
      </RadixSelect.Trigger>
      <RadixSelect.Portal>
        <RadixSelect.Content position="popper" className={contentClassName}>
          <RadixSelect.Viewport>
            {options.map((option) => (
              <RadixSelect.Item
                key={option.value}
                value={option.value}
                className={itemClassName}
              >
                <RadixSelect.ItemIndicator>
                  <Check size={16} />
                </RadixSelect.ItemIndicator>
                <RadixSelect.ItemText>{option.label}</RadixSelect.ItemText>
              </RadixSelect.Item>
            ))}
          </RadixSelect.Viewport>
        </RadixSelect.Content>
      </RadixSelect.Portal>
    </RadixSelect.Root>
  );
}

export interface MultiSelectProps {
  id?: string;
  values: string[];
  onValuesChange?: (values: string[]) => void;
  options: SelectOption[];
  placeholder?: string;
  removeLabel?: string;
  disabled?: boolean;
}

/** Chips with an explicit remove control, plus a picker for what is left. */
export function MultiSelect({
  values,
  onValuesChange,
  options,
  placeholder,
  removeLabel = "Remove",
  disabled,
  id,
}: MultiSelectProps) {
  const remaining = options.filter((option) => !values.includes(option.value));
  const labelOf = (value: string) =>
    options.find((option) => option.value === value)?.label ?? value;
  return (
    <div className="flex flex-col gap-2">
      {values.length > 0 && (
        <ul className="flex flex-wrap gap-2">
          {values.map((value) => (
            <li key={value}>
              <span className="inline-flex items-center gap-1 rounded-full bg-accent-subtle py-1 pl-3 pr-1.5 text-footnote font-medium text-accent-fg">
                {labelOf(value)}
                <button
                  type="button"
                  disabled={disabled}
                  aria-label={`${removeLabel}: ${labelOf(value)}`}
                  className="relative inline-flex size-4 items-center justify-center rounded-sm after:absolute after:size-11 after:content-['']"
                  onClick={() => {
                    onValuesChange?.(values.filter((item) => item !== value));
                  }}
                >
                  <Close size={14} />
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
      {remaining.length > 0 && (
        <Select
          id={id}
          options={remaining}
          placeholder={placeholder}
          disabled={disabled}
          onValueChange={(value) => {
            onValuesChange?.([...values, value]);
          }}
        />
      )}
    </div>
  );
}

export interface ReferenceSelectProps extends SelectProps {
  /** Human-readable value for the referenced record. */
  displayValue?: ReactNode;
}

/**
 * A reference renders as a human-readable name, never a raw id — the record
 * identifier stays out of the interface except in print mode.
 */
export function ReferenceSelect({
  displayValue,
  ...props
}: ReferenceSelectProps) {
  return (
    <div className="flex items-center gap-2">
      <span
        aria-hidden="true"
        className="size-2 shrink-0 rounded-full bg-accent"
      />
      <Select {...props} />
      {displayValue !== undefined && (
        <span className="inline-flex items-center gap-1 text-footnote text-fg-muted">
          <ChevronRight size={16} />
          {displayValue}
        </span>
      )}
    </div>
  );
}
